/**
 * StaticAtom - Motion-enabled atom component with real graphics
 *
 * Simple wrapper that delegates coordinate updates.
 *
 * Wrapped with React.memo (PIPH-69) so that re-renders of the parent
 * layer don't cascade into re-renders of every atom. Since useSVGGeneration
 * memoizes its output, the `atom` prop is reference-stable across unrelated
 * re-renders — selection, hover, and other context changes no longer force
 * every StaticAtom to re-render.
 */

import { memo, useRef } from 'react';
import { useMotion } from '../../contexts/MotionContext';
import { motion } from 'motion/react';
import type { AtomRenderData } from '../../rendering/useSVGGeneration';

export interface StaticAtomProps {
  atom: AtomRenderData;
  /**
   * Callback to get translation group when drag starts.
   * Returns array of atom IDs if all atoms in molecule are selected (full translation).
   * Typically provided by useAnchorOrbit hook.
   */
  getTranslationGroup?: (draggedAtomId: string) => string[] | null;
}

export const StaticAtom = memo(function StaticAtom({
  atom,
}: StaticAtomProps) {
  const motionContext = useMotion();
  const motionValues = motionContext.getAtomMotion(atom.id);
  const gRef = useRef<SVGGElement>(null);

  // Visibility wrapper needed for both paths
  const isHidden = atom.visibility === 'hidden';

  if (!motionValues) {
    // No motion context - render static
    return (
      <g style={{ display: isHidden ? 'none' : undefined }}>
        <g transform={`translate(${atom.x}, ${atom.y})`}>
          {atom.graphics}
        </g>
      </g>
    );
  }

  // Wrap in visibility container - motion.g can't mix MotionValues with display CSS

  return (
    <g style={{ display: isHidden ? 'none' : undefined, pointerEvents: 'none', userSelect: 'none' }}>
      <motion.g
        ref={gRef}
        style={{
          x: motionValues.x,
          y: motionValues.y,
        }}
      whileHover={{ scale: 1.1 }}
      >
        <g>
          {atom.graphics}
        </g>
      </motion.g>
    </g>
  );
});
